/**
 * QC problem-tag catalogue shared with the backend contract.
 *
 * The 13 tag strings are a FROZEN contract with the QC-note API (Todos 3-5 of
 * the persistent-QC integration plan). Reorder / rename ONLY together with
 * the backend enum; a drift on either side breaks parse validation in
 * `qcNoteService.ts`.
 */
export const QC_PROBLEM_TAGS = [
  "Gender",
  "Missing letter",
  "Need split",
  "Need merge",
  "Wrong translation",
  "Timing",
  "Slang",
  "Male to male",
  "Male to female",
  "Male to many",
  "Female to female",
  "Female to male",
  "Female to many",
] as const;

export type QCProblemTag = typeof QC_PROBLEM_TAGS[number];

/** Mirrors the QC-note limits enforced by the backend; changing either side alone rejects saves. */
export const MAX_NOTE_LINES = 6;
export const MAX_NOTE_CHARACTERS = 1000;

export const limitQCNoteLines = (text: string): string =>
  text.split("\n").slice(0, MAX_NOTE_LINES).join("\n");

export const hasQCNoteComment = (text: string, problemTag: QCProblemTag | null): boolean => {
  const prefix = problemTag === null ? "" : `${problemTag}:`;
  const comment = prefix && text.startsWith(prefix) ? text.slice(prefix.length) : text;
  return comment.trim().length > 0;
};

export const canSaveQCNote = (text: string, problemTag: QCProblemTag | null): boolean =>
  problemTag !== null || hasQCNoteComment(text, problemTag);

const synchronizedPrefix = (tag: QCProblemTag): string => `${tag}: `;

export const stripSynchronizedTagPrefix = (text: string, problemTag: QCProblemTag | null): string => {
  if (problemTag === null) return text;
  const prefix = synchronizedPrefix(problemTag);
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
};

export const withSynchronizedTagPrefix = (text: string, problemTag: QCProblemTag | null): string => {
  if (problemTag === null) return text;
  const prefix = synchronizedPrefix(problemTag);
  return text.startsWith(prefix) ? text : `${prefix}${text}`;
};
